import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Receipt,
  Building2,
  User,
  Calendar,
  Download,
  FileText,
  Filter,
  Eye
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { rentPaymentsAPI, propertiesAPI, clientsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const paymentMethodLabels = {
  especes: 'Especes',
  virement: 'Virement',
  mobile_money: 'Mobile Money',
  cheque: 'Cheque'
};

const AllReceipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterTenant, setFilterTenant] = useState('all');
  
  // Modal states
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [receiptsRes, propRes, clientRes] = await Promise.all([
        rentPaymentsAPI.getAll(),
        propertiesAPI.getAll(),
        clientsAPI.getAll()
      ]);
      setReceipts(receiptsRes.data);
      setProperties(propRes.data.filter(p => p.transaction_type === 'location'));
      setTenants(clientRes.data.filter(c => c.client_type === 'locataire'));
    } catch (error) {
      toast.error('Erreur lors du chargement des quittances');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (receipt) => {
    setSelectedReceipt(receipt);
    setIsPreviewOpen(true);
  };

  const handleDownloadPDF = async (receiptId, receiptNumber) => {
    setDownloading(receiptId);
    try {
      const response = await rentPaymentsAPI.downloadPDF(receiptId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quittance_${receiptNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Quittance telecharger avec succes');
    } catch (error) {
      toast.error('Erreur lors du telechargement de la quittance');
    } finally {
      setDownloading(null);
    }
  };

  const filteredReceipts = receipts.filter(receipt => {
    const matchesSearch = 
      (receipt.property_title && receipt.property_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (receipt.tenant_name && receipt.tenant_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (receipt.receipt_number && receipt.receipt_number.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesProperty = filterProperty === 'all' || receipt.property_id === filterProperty;
    const matchesTenant = filterTenant === 'all' || receipt.tenant_id === filterTenant;
    
    return matchesSearch && matchesProperty && matchesTenant;
  });

  // Group receipts by tenant for summary
  const receiptsByTenant = {};
  receipts.forEach(receipt => {
    if (!receiptsByTenant[receipt.tenant_id]) {
      receiptsByTenant[receipt.tenant_id] = {
        tenant_name: receipt.tenant_name,
        count: 0,
        total: 0
      };
    }
    receiptsByTenant[receipt.tenant_id].count += 1;
    receiptsByTenant[receipt.tenant_id].total += receipt.amount;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="all-receipts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Toutes les Quittances
          </h1>
          <p className="text-gray-500 mt-1">
            {receipts.length} quittance{receipts.length > 1 ? 's' : ''} au total
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-[#1B3A4B] to-[#142D3A] text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Receipt className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Total Quittances</p>
                <p className="text-2xl font-bold">{receipts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-[#D4A373] to-[#b8895f] text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <User className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Locataires</p>
                <p className="text-2xl font-bold">{Object.keys(receiptsByTenant).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Montant Total</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(receipts.reduce((sum, r) => sum + r.amount, 0), 'XOF')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par bien, locataire ou numero..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-receipts-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterProperty} onValueChange={setFilterProperty}>
                <SelectTrigger className="w-[200px]" data-testid="filter-property-select">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrer par bien" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les biens</SelectItem>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterTenant} onValueChange={setFilterTenant}>
                <SelectTrigger className="w-[200px]" data-testid="filter-tenant-select">
                  <User className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrer par locataire" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les locataires</SelectItem>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receipts Table */}
      {filteredReceipts.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N Quittance</TableHead>
                  <TableHead>Bien</TableHead>
                  <TableHead>Locataire</TableHead>
                  <TableHead>Periode</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Date Paiement</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReceipts.map((receipt) => (
                  <TableRow key={receipt.id}>
                    <TableCell>
                      <span className="font-mono text-sm text-[#1B3A4B] font-semibold">
                        {receipt.receipt_number}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="truncate max-w-[150px]">
                          {receipt.property_title || 'Bien supprime'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{receipt.tenant_name || 'Locataire supprime'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        {formatDate(receipt.period_start)} - {formatDate(receipt.period_end)}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-[#1B3A4B]">
                      {formatCurrency(receipt.amount, receipt.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {paymentMethodLabels[receipt.payment_method]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-500">
                      {formatDate(receipt.payment_date)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePreview(receipt)}
                          data-testid={`preview-receipt-${receipt.id}`}
                          title="Apercu"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownloadPDF(receipt.id, receipt.receipt_number)}
                          disabled={downloading === receipt.id}
                          data-testid={`download-receipt-${receipt.id}`}
                          title="Telecharger PDF"
                          className="text-[#1B3A4B] hover:text-[#142D3A] hover:bg-[#1B3A4B]/10"
                        >
                          {downloading === receipt.id ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Receipt className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune quittance trouvee</h3>
              <p className="text-gray-500 mb-4">
                {searchQuery || filterProperty !== 'all' || filterTenant !== 'all'
                  ? 'Essayez de modifier vos filtres'
                  : 'Les quittances apparaitront ici une fois les paiements enregistres'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Receipt Preview Modal */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Apercu de la Quittance</DialogTitle>
          </DialogHeader>
          
          {selectedReceipt && (
            <div className="print:p-8" id="receipt-preview-content">
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                {/* Header */}
                <div className="flex justify-between items-start mb-6 pb-4 border-b">
                  <div>
                    <h2 className="text-2xl font-bold text-[#1B3A4B]">GAB'S SERVICES HABITAT</h2>
                    <p className="text-sm text-gray-500">Gestion Immobiliere</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold">QUITTANCE DE LOYER</p>
                    <p className="text-sm text-gray-500">N {selectedReceipt.receipt_number}</p>
                  </div>
                </div>
                
                {/* Details */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Locataire</h3>
                    <p className="font-semibold">{selectedReceipt.tenant_name}</p>
                    <p className="text-sm text-gray-600">{selectedReceipt.tenant_phone}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">Bien loue</h3>
                    <p className="font-semibold">{selectedReceipt.property_title}</p>
                    <p className="text-sm text-gray-600">{selectedReceipt.property_address}</p>
                  </div>
                </div>
                
                {/* Payment Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Periode</p>
                      <p className="font-medium">
                        Du {formatDate(selectedReceipt.period_start)} au {formatDate(selectedReceipt.period_end)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Date de paiement</p>
                      <p className="font-medium">{formatDate(selectedReceipt.payment_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mode de paiement</p>
                      <p className="font-medium">{paymentMethodLabels[selectedReceipt.payment_method]}</p>
                    </div>
                    {selectedReceipt.reference && (
                      <div>
                        <p className="text-sm text-gray-500">Reference</p>
                        <p className="font-medium">{selectedReceipt.reference}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Amount */}
                <div className="text-center py-4 border-t border-b mb-6">
                  <p className="text-sm text-gray-500 mb-1">Montant paye</p>
                  <p className="text-3xl font-bold text-[#1B3A4B]">
                    {formatCurrency(selectedReceipt.amount, selectedReceipt.currency)}
                  </p>
                </div>
                
                {/* Footer */}
                <div className="text-center text-sm text-gray-500">
                  <p>Cette quittance atteste du paiement du loyer pour la periode indiquee.</p>
                  <p className="mt-2">Emise le {formatDate(selectedReceipt.created_at)}</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
              Fermer
            </Button>
            <Button 
              onClick={() => handleDownloadPDF(selectedReceipt?.id, selectedReceipt?.receipt_number)}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              disabled={downloading === selectedReceipt?.id}
              data-testid="download-preview-receipt-btn"
            >
              {downloading === selectedReceipt?.id ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Telechargement...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Telecharger PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AllReceipts;
